import { memo } from 'react';
import classNames from 'classnames';
import styles from './Banner.module.scss';
import ESIInclude from '../../esi';
import { useAdtechBannerData } from './data/useAdtechBannerData';

type AdtechProps = {
  brandName?: string;
  pathname?: string;
};

type BrandProps = {
  brandName: string;
};

type SearchBrowseProps = {
  pathname: string;
};

const AdtechBrowseBanner = memo(({ pathname }: SearchBrowseProps) => {
  const { applicationSettings, breadcrumbContext, facetContext } =
    useAdtechBannerData();
  const { adtechBannerService } = applicationSettings;
  const { breadcrumbs } = breadcrumbContext;

  if (!Array.isArray(breadcrumbs) || !breadcrumbs?.length) return null;

  const formattedBreadcrumbs = encodeURIComponent(
    breadcrumbs
      .filter((b) => b?.displayName)
      .map((b) => b.displayName.trim())
      .join('|')
  );

  const { appliedProductTypes, isFilteredByBrand } = facetContext;
  const appliedProductTypesString =
    Array.isArray(appliedProductTypes) && appliedProductTypes.length
      ? `&appliedProductTypes=${appliedProductTypes.join('|')}`
      : '';

  const brandFilterAppliedString = isFilteredByBrand
    ? '&brandFilterApplied=true'
    : '&brandFilterApplied=false';

  // pathname is an arbitrary string used to trigger new ad impressions in the ESI
  const pathnameString = pathname ? `&pathname=${pathname}` : '';

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanners)}
    >
      <ESIInclude
        src={`${adtechBannerService}/browse?browsePageHierarchy=${formattedBreadcrumbs}${brandFilterAppliedString}${pathnameString}${appliedProductTypesString}`}
        id="esiAdtechBanner"
      />
    </aside>
  );
});
AdtechBrowseBanner.displayName = 'AdtechBrowseBanner';

const AdtechSearchBanner = memo(({ pathname }: SearchBrowseProps) => {
  const { applicationSettings, facetContext, router } = useAdtechBannerData();
  const { adtechBannerService } = applicationSettings;
  const { isFiltered } = facetContext;
  const { searchTerm } = router;

  if (!searchTerm) return null;

  const isFilteredString = isFiltered
    ? '&filterApplied=true'
    : '&filterApplied=false';

  // pathname is an arbitrary string used to trigger new ad impressions in the ESI
  const pathnameString = pathname ? `&pathname=${pathname}` : '';

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanners)}
    >
      <ESIInclude
        src={`${adtechBannerService}/search?searchTerm=${encodeURIComponent(
          searchTerm.toString()
        )}${isFilteredString}${pathnameString}`}
        id="esiAdtechBanner"
      />
    </aside>
  );
});
AdtechSearchBanner.displayName = 'AdtechSearchBanner';

const AdtechBrandBanner = memo(({ brandName }: BrandProps) => {
  const { applicationSettings } = useAdtechBannerData();
  const { adtechBannerService } = applicationSettings;

  if (!brandName) return null;

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanners)}
    >
      <ESIInclude
        src={`${adtechBannerService}/brand?brandName=${encodeURIComponent(
          brandName.toString()
        )}`}
        id="esiAdtechBanner"
      />
    </aside>
  );
});
AdtechBrandBanner.displayName = 'AdtechBrandBanner';

export const AdtechBanner = memo(({ brandName, pathname }: AdtechProps) => {
  const { router } = useAdtechBannerData();
  const { searchTerm, type } = router;

  if (type === 'browse' && pathname) {
    return <AdtechBrowseBanner pathname={pathname} />;
  }

  if (type === 'search' && typeof searchTerm === 'string' && pathname) {
    return <AdtechSearchBanner pathname={pathname} />;
  }

  if (type === 'brand' && brandName) {
    return <AdtechBrandBanner brandName={brandName} />;
  }

  return null;
});
AdtechBanner.displayName = 'AdtechBanner';
